/* eslint-disable max-lines */
import { MONTH_NAMES } from "../../constants/common/common";
import { constants } from "../../constants/pageConstants/pdpConstants";
import { convert24To12Format } from "../common";
import { calenderIsTodayOrTomorrowDate, getEarliestDeliveryDate } from "../common/deliveryDate";
import { resolveCheckoutServiceabilityPinCode } from "./serviceabilityForPincode";

/**
 * Maps checkout gift item to PDP shipping component shape.
 *
 * @param {Object} giftItem
 * @returns {Object}
 */
export const mapGiftItemToCurrentItem = (giftItem = {}) => {
  const mainproduct = giftItem?.mainproduct || {};

  return {
    productId: mainproduct?.productId,
    cartItemIndex: giftItem?.cartItemIndex,
    primaryCategoryId: mainproduct?.primaryProductCategoryId,
    primaryProductCategoryId: mainproduct?.primaryProductCategoryId,
    productCatalogId: giftItem?.productCatalogId,
    catalogId: giftItem?.productCatalogId,
    countryGeoId: giftItem?.countryGeoId || giftItem?.selectedAddressDetails?.countryGeoId || "IND",
    pincode: mainproduct?.pincode,
    productName: mainproduct?.productName,
    productPrice: { price: mainproduct?.price, listPrice: mainproduct?.listPrice },
    shippingDetails: giftItem?.shippingDetails,
    urlIdentifier: giftItem?.productUrl,
    isMultiOrderProduct: giftItem?.isMultiOrderProduct,
  };
};

/**
 * Resolves checkout pincode/cityId for serviceability APIs.
 *
 * @param {Object} giftItem
 * @returns {string}
 */
export const getCheckoutServiceabilityPinCode = (giftItem = {}) => {
  return resolveCheckoutServiceabilityPinCode(giftItem, giftItem?.productCatalogId);
};

/**
 * Resolves selected delivery date from gift item.
 *
 * @param {Object} giftItem
 * @returns {string}
 */
export const getCheckoutDeliveryDate = (giftItem = {}) => {
  return giftItem?.shippingDetails?.deliveryDate?.fullDeliveryDate || giftItem?.deliveryDate || "";
};

/**
 * Resolves checkout drawer initial date using cart selection and earliest available date.
 * Keeps the cart date when it is still valid; falls back to earliest when cart date is expired.
 *
 * @param {Object} giftItem
 * @param {Array<string>} deliveryDatesList
 * @returns {string}
 */
export const resolveCheckoutDrawerInitialDate = (giftItem = {}, deliveryDatesList = []) => {
  const itemSelectedDate = getCheckoutDeliveryDate(giftItem);
  const earliestAvailableDate = deliveryDatesList?.[0] || "";

  if (!earliestAvailableDate) {
    return itemSelectedDate;
  }

  return getEarliestDeliveryDate(earliestAvailableDate, itemSelectedDate);
};

/**
 * Checks whether gift item has shipping method and time slot selected.
 *
 * @param {Object} giftItem
 * @returns {boolean}
 */
export const hasCheckoutShippingSelection = (giftItem = {}) => {
  return Boolean(
    giftItem?.shippingDetails?.deliveryTimeSlotId &&
    giftItem?.shippingDetails?.shippingMethodId &&
    giftItem?.shippingDetails?.deliveryTimeSlot,
  );
};

/**
 * Returns whether a checkout gift item still needs a delivery time slot.
 *
 * @param {Object} giftItem
 * @returns {boolean}
 */
export const isCheckoutGiftItemTimeSlotPending = (giftItem = {}) => {
  const deliveryTimeSlot = giftItem?.shippingDetails?.deliveryTimeSlot;

  return deliveryTimeSlot === undefined || deliveryTimeSlot === null || String(deliveryTimeSlot).trim() === "";
};

/**
 * Filters order list to items that still need a time slot in split-cart wizard.
 *
 * @param {Array} orderList
 * @returns {Array}
 */
export const getSplitCartPendingTimeSlotOrderList = (orderList = []) =>
  orderList.filter((order) => isCheckoutGiftItemTimeSlotPending(order?.giftItem));

/**
 * Builds pending drawer delivery details from an existing cart shipping selection.
 *
 * @param {Object} giftItem
 * @param {string} selectedDate
 * @returns {Object|null}
 */
export const buildPendingDeliveryDetailsFromGiftItem = (giftItem = {}, selectedDate = "") => {
  const shippingDetails = giftItem?.shippingDetails || {};

  if (!hasCheckoutShippingSelection(giftItem)) {
    return null;
  }

  const timeSlotId = shippingDetails.deliveryTimeSlotId;
  const isSuperDynamicSlot = timeSlotId === "SUPER_DYNAMIC_SLOT" || shippingDetails.isDynamic === "Y";
  const timeRange = shippingDetails.deliveryTimeSlot?.split("Hrs")[0]?.trim() || "";
  const [parsedStart = "", parsedEnd = ""] = timeRange.split(" - ").map((value) => value.trim());
  const startTime = shippingDetails.startTime || parsedStart || "";
  const endTime = shippingDetails.endTime || parsedEnd || "";
  const dynamicSlotText = isSuperDynamicSlot ? shippingDetails.dynamicSlotText || constants?.THIRTY_SIXTY_MINUTES : "";

  return {
    selectedDate,
    currentShippingMethod: {
      shippingMethodId: shippingDetails.shippingMethodId || "",
      shippingMethodName: shippingDetails.shippingMethodName || "",
    },
    timeSlots: {
      timeSlots: {
        timeSlotId,
        shippingPrice: shippingDetails.shippingPrice,
        vendors: shippingDetails.vendors || shippingDetails.suppliers,
        ...(isSuperDynamicSlot
          ? {
              isDynamic: shippingDetails.isDynamic || "Y",
              dynamicSlotText,
              startTime,
              endTime,
            }
          : {}),
      },
      formatTime:
        shippingDetails.deliveryTimeSlot?.replace(/\s*Hrs$/i, "")?.trim() || shippingDetails.deliveryTimeSlot || "",
    },
  };
};

/**
 * Resolves shipping selection time slot id from gift item.
 *
 * @param {Object} giftItem
 * @returns {string}
 */
export const getShippingSelectionTimeSlotId = (giftItem = {}) => {
  return giftItem?.shippingDetails?.deliveryTimeSlotId || "";
};

/**
 * Finds a time slot from shipping API response by method and slot id.
 *
 * @param {Object} shippingResponse
 * @param {string} shippingMethodId
 * @param {string} timeSlotId
 * @returns {Object|null}
 */
export const findShippingSlotByMethodAndId = (shippingResponse = {}, shippingMethodId = "", timeSlotId = "") => {
  if (!shippingMethodId || !timeSlotId || !Array.isArray(shippingResponse?.data)) {
    return null;
  }

  const methodEntry = shippingResponse.data.find((item) => Object.keys(item)[0] === String(shippingMethodId));
  if (!methodEntry) {
    return null;
  }

  const methodKey = Object.keys(methodEntry)[0];
  const methodDetails = methodEntry[methodKey];
  const slots = methodDetails?.timeSlots || [];

  return slots.find((slot) => String(slot?.timeSlotId) === String(timeSlotId)) || null;
};

/**
 * Resolves the raw Ttotal JSON string from checkout shipping-details response.
 *
 * @param {Object} singleResponse
 * @param {Object} combinedResponse
 * @param {Object|null} shippingOverride
 * @returns {string|undefined}
 */
export const resolveCheckoutShippingTTotal = (singleResponse = {}, combinedResponse = {}, shippingOverride = null) => {
  const combinedSource = shippingOverride || combinedResponse;
  const tTotalRaw =
    combinedSource?.Ttotal || singleResponse?.Ttotal || combinedSource?.data?.Ttotal || singleResponse?.data?.Ttotal;

  if (tTotalRaw === undefined || tTotalRaw === null || tTotalRaw === "") {
    return undefined;
  }

  if (typeof tTotalRaw === "string") {
    return tTotalRaw;
  }

  try {
    return JSON.stringify(tTotalRaw);
  } catch {
    return undefined;
  }
};

/**
 * Builds setCartItemValues payload for checkout shipping update.
 *
 * @param {Object} params
 * @returns {string}
 */
export const buildCheckoutShippingCartPayload = ({
  cartItemIndex,
  selectedDate,
  shippingMethodId,
  timeSlotId,
  shippingPrice,
  vendors,
  resolvedSupplierPartyId,
  dynamicSlotText,
  startTime,
  endTime,
  tTotal,
}) => {
  const payload = {
    itemIndex: String(cartItemIndex),
    deliveryDate: selectedDate,
  };

  if (shippingMethodId) {
    payload.shippingMethodId = shippingMethodId;
  }

  if (shippingPrice !== undefined && shippingPrice !== null) {
    payload.shippingPrice = shippingPrice;
  }

  if (timeSlotId) {
    payload.timeSlotId = timeSlotId;
  }

  if (vendors) {
    payload.suppliers = vendors;
  }

  if (resolvedSupplierPartyId) {
    payload.resolvedSupplierPartyId = resolvedSupplierPartyId;
  }

  if (timeSlotId === "SUPER_DYNAMIC_SLOT") {
    if (dynamicSlotText) {
      payload.dynamicSlotText = dynamicSlotText;
    }

    if (startTime && endTime) {
      payload.dynamicStartTime = startTime;
      payload.dynamicEndTime = endTime;
    }
  }

  if (tTotal) {
    payload.tTotal = tTotal;
  }

  return new URLSearchParams(payload).toString();
};

/**
 *
 * @param {string} dateString
 * @returns {string}
 */
export const formatCheckoutDeliveryDateLabel = (dateString = "", isFromHeader = false) => {
  if (!dateString) {
    return "";
  }

  const { isDateToday, isDateTomorrow } = calenderIsTodayOrTomorrowDate(dateString);
  const [day, month, year] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const monthName = MONTH_NAMES[date.getMonth()];

  if (isDateToday) {
    return isFromHeader ? `Today` : `Today, ${day} ${monthName}`;
  }

  if (isDateTomorrow) {
    return isFromHeader ? `Tomorrow` : `Tomorrow, ${day} ${monthName}`;
  }

  return `${day} ${monthName}`;
};

/**
 * Formats checkout delivery header summary for DA section tile.
 *
 * @param {Object} giftItem
 * @param {Object} selectedCurrency
 * @returns {{ primaryLine: string, secondaryLine: string, hasSelection: boolean, isFree?: boolean, listPrice?: number }}
 */
export const formatCheckoutDeliveryHeaderSummary = (giftItem = {}, selectedCurrency = {}, isFromHeader = false) => {
  const shippingDetails = giftItem?.shippingDetails || {};
  const hasSelection = hasCheckoutShippingSelection(giftItem);
  const isInstantDelivery = shippingDetails?.deliveryTimeSlotId === "SUPER_DYNAMIC_SLOT";

  if (!hasSelection) {
    return {
      hasSelection: false,
      primaryLine: "",
      secondaryLine: "",
    };
  }

  const formattedDate = formatCheckoutDeliveryDateLabel(shippingDetails?.deliveryDate?.fullDeliveryDate, isFromHeader);
  const time = shippingDetails?.deliveryTimeSlot?.split("Hrs")[0] || "";
  const startTime = time?.split(" - ")[0] || "";
  const endTime = time?.split(" - ")[1] || "";
  const timeRange = convert24To12Format(startTime, endTime);
  const price = Number(shippingDetails?.shippingPrice);
  const isFree = Number.isFinite(price) && price === 0;
  const shippingMethodName = isInstantDelivery
    ? constants?.INSTANT_DELIVERY
    : shippingDetails?.shippingMethodName?.trim() || "";

  return {
    hasSelection: true,
    primaryLine: isInstantDelivery
      ? shippingDetails?.dynamicSlotText || constants?.THIRTY_SIXTY_MINUTES
      : `${formattedDate}${timeRange ? `, ${timeRange}` : ""}`,
    secondaryLine: shippingMethodName,
    isFree,
    price: shippingDetails?.shippingPrice,
    selectedCurrency,
    listPrice: shippingDetails?.shippingListPrice,
  };
};

/**
 * Compares checkout delivery dates that may use different separators.
 *
 * @param {string} dateA
 * @param {string} dateB
 * @returns {boolean}
 */
export const areCheckoutDatesEqual = (dateA = "", dateB = "") => {
  if (!dateA || !dateB) {
    return false;
  }

  /**
   * Normalizes date string to YYYY-MM-DD format.
   *
   * @param {string} date
   * @returns {string}
   */
  const normalizeDate = (date) => date.replace(/\//g, "-").trim();
  return normalizeDate(dateA) === normalizeDate(dateB);
};

/**
 * Maps legacy shipping API data array to method id/name pairs.
 *
 * @param {Array} shippingData
 * @returns {Array<{ shippingMethodId: string, shippingMethodName: string }>}
 */
export const mapShippingMethodsFromResponse = (shippingData = []) => {
  return shippingData.map((shipping) => {
    const shippingMethodId = Object.keys(shipping)[0];
    return {
      shippingMethodId,
      shippingMethodName: shipping[shippingMethodId]?.shippingMethodName,
    };
  });
};

/**
 * Finds a shipping method from API list using saved id or name.
 *
 * @param {Array<{ shippingMethodId: string, shippingMethodName: string }>} shippingList
 * @param {{ shippingMethodId?: string, shippingMethodName?: string }} candidate
 * @returns {{ shippingMethodId: string, shippingMethodName: string }|null}
 */
export const findMatchingShippingMethod = (shippingList = [], candidate = {}) => {
  if (!shippingList?.length) {
    return null;
  }

  const { shippingMethodId, shippingMethodName } = candidate;

  if (shippingMethodId) {
    const matchedById = shippingList?.find(({ shippingMethodId: id }) => id === shippingMethodId);
    if (matchedById) {
      return matchedById;
    }
  }

  if (shippingMethodName) {
    const matchedByName = shippingList?.find(({ shippingMethodName: name }) => name === shippingMethodName);
    if (matchedByName) {
      return matchedByName;
    }
  }

  return null;
};

/**
 * Resolves legacy shipping response shape for single or combined checkout.
 *
 * @param {Object} singleResponse
 * @param {Object} combinedResponse
 * @param {Object|null} shippingOverride
 * @returns {Object}
 */
export const resolveCheckoutShippingResponse = (
  singleResponse = {},
  combinedResponse = {},
  shippingOverride = null,
) => {
  if (Array.isArray(singleResponse?.data) && singleResponse.data.length) {
    return singleResponse;
  }

  const combinedSource = shippingOverride || combinedResponse;

  if (Array.isArray(combinedSource?.data) && combinedSource.data.length) {
    return combinedSource;
  }

  const firstProductEntry = Object.values(combinedSource?.data || {}).find(
    (entry) => Array.isArray(entry?.shippingDetails?.data) && entry.shippingDetails.data.length,
  );

  return firstProductEntry?.shippingDetails || {};
};

/**
 * Converts new checkout shipping methods map to legacy array format.
 *
 * @param {Object} methodsMap
 * @returns {Array}
 */
const convertMethodsMapToLegacyArray = (methodsMap = {}) => {
  return Object.entries(methodsMap).map(([methodId, details]) => ({
    [methodId]: details,
  }));
};

/**
 * @param {Object} value
 * @returns {boolean}
 */
const isShippingMethodsMap = (value) => {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.values(value).some((entry) => entry && typeof entry === "object" && entry.shippingMethodName)
  );
};

/**
 * Normalizes a shipping details block (legacy or new checkout format) to legacy shape.
 *
 * @param {Object} shippingDetails
 * @returns {Object}
 */
export const normalizeShippingDetailsBlock = (shippingDetails) => {
  if (!shippingDetails) {
    return {
      data: [],
      hasInstantDelivery: false,
      selectedShippingMethod: "",
      selectedTimeSlot: "",
    };
  }

  if (Array.isArray(shippingDetails.data)) {
    return shippingDetails;
  }

  if (isShippingMethodsMap(shippingDetails)) {
    return {
      data: convertMethodsMapToLegacyArray(shippingDetails),
      hasInstantDelivery: shippingDetails.hasInstantDelivery ?? false,
      selectedShippingMethod: shippingDetails.selectedShippingMethod ?? "",
      selectedTimeSlot: shippingDetails.selectedTimeSlot ?? "",
    };
  }

  return shippingDetails;
};

/**
 * Normalizes single-item checkout shipping-details API response to legacy PDP format.
 *
 * @param {Object} response
 * @returns {Object}
 */
export const normalizeCheckoutSingleShippingResponse = (response = {}) => {
  if (Array.isArray(response?.data)) {
    return response;
  }

  const rootData = response?.data || {};
  const shippingBlock = normalizeShippingDetailsBlock(rootData.shippingDetails);

  return {
    data: shippingBlock.data,
    hasInstantDelivery: shippingBlock.hasInstantDelivery ?? response.hasInstantDelivery ?? false,
    isCached: response.isCached ?? "N",
    responseStatus: rootData.isSuccess !== false ? "success" : "failure",
    selectedShippingMethod: shippingBlock.selectedShippingMethod ?? "",
    selectedTimeSlot: shippingBlock.selectedTimeSlot ?? "",
    status: response.status ?? 200,
    Ttotal: rootData.Ttotal ?? response.Ttotal ?? "",
  };
};

/**
 * Normalizes checkout shipping-details API response based on cart type.
 *
 * @param {Object} response
 * @param {{ isCombinedCheckout?: boolean }} options
 * @returns {Object}
 */
export const normalizeCheckoutShippingDetailsResponse = (response = {}) => {
  return normalizeCheckoutSingleShippingResponse(response);
};

/**
 * Builds comma-separated product ids for combined checkout API.
 *
 * @param {Array} orderList
 * @returns {string}
 */
export const getCombinedCheckoutProductIds = (orderList = []) => {
  return orderList
    .map((order) => order?.giftItem?.mainproduct?.productId)
    .filter(Boolean)
    .join(",");
};

/**
 * Checks whether any shipping method in data has free delivery.
 *
 * @param {Array|Object} shippingData
 * @returns {boolean}
 */
export const hasFreeDeliveryInShippingData = (shippingData = []) => {
  const shippingList = Array.isArray(shippingData) ? shippingData : shippingData?.data;
  if (!Array.isArray(shippingList)) {
    return false;
  }

  return shippingList.some((method) => Object.values(method)?.[0]?.priceDetails?.price === 0);
};

/**
 * Checks whether free delivery exists in single-item checkout shipping response.
 *
 * @param {Object} shippingResponse
 * @returns {boolean}
 */
export const hasSingleCheckoutFreeDelivery = (shippingResponse = {}) => {
  return hasFreeDeliveryInShippingData(shippingResponse?.data);
};

/**
 * Checks whether combined cart has shipping selection (shared across all items).
 *
 * @param {Array} orderList
 * @returns {boolean}
 */
export const hasCombinedCheckoutShippingSelection = (orderList = []) => {
  return hasCheckoutShippingSelection(orderList[0]?.giftItem);
};

/**
 * Resolves reference gift item for combined cart header (first item).
 *
 * @param {Array} orderList
 * @returns {Object}
 */
export const getCombinedCheckoutHeaderGiftItem = (orderList = []) => {
  return orderList[0]?.giftItem || {};
};

/**
 * Returns comma-separated cart item indexes for combined cart save API.
 *
 * @param {Array} orderList
 * @returns {string}
 */
export const getCombinedCheckoutCartItemIndexes = (orderList = []) => {
  if (!orderList.length) {
    return "";
  }

  const indexes = orderList.map((order) => order?.giftItem?.cartItemIndex);

  if (indexes.every((index) => index !== undefined && index !== null && index !== "")) {
    return indexes.map(String).join(",");
  }

  return Array.from({ length: orderList.length }, (_, index) => index).join(",");
};

/**
 * Builds setCartItemValues payload to sync resolved supplier for auto-combined cart items.
 *
 * @param {{ orderList?: Array, resolvedSupplierPartyId?: string|number }} params
 * @returns {string|null}
 */
export const buildAutoCombinedCartSupplierPayload = ({ orderList = [], resolvedSupplierPartyId = "" } = {}) => {
  const itemIndex = getCombinedCheckoutCartItemIndexes(orderList);

  if (!itemIndex || !resolvedSupplierPartyId) {
    return null;
  }

  return new URLSearchParams({
    itemIndex,
    resolvedSupplierPartyId: String(resolvedSupplierPartyId),
  }).toString();
};

/**
 * @param {Object} giftItem
 * @returns {boolean}
 */
export const isCheckoutInternationalGiftItem = (giftItem = {}) => {
  return giftItem?.countryGeoId !== "IND";
};
